import { EpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { IEpticaRequestFrame } from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { ContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NG1_CONTACT, NG1_EPTICA } from '@commons/core/core.provider.ajs';
import { destroy } from '@commons/util/utilities/rx';
import { IPromise, isDefined } from 'angular';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import ResizeSensor from 'resize-sensor';
import { BehaviorSubject } from 'rxjs';

import { Logger, Memo, MomentService, Option } from '@contact/upgrade';

import { IEpticaExctractAttachment, IEpticaExctractEvent, IEpticaMailContent } from './models/eptica-extract';
import { EpticaSelector } from './models/eptica-selector';

enum DescriptionStatus {
  EMAILCONTENT = 'emailcontent',
  EMAILINFO = 'emailinfo',
  EMAILATTACHMENT = 'emailattachment',
}

interface ITabData {
  actualSize: string;
  minSize: string;
  count: number;
}

interface IBreakpoint {
  breakpointName: string;
  breakpointValue: string;
}

@Component({
  selector: 'history-mail',
  templateUrl: './history-mail.component.html',
  styleUrls: ['./history-mail.component.scss'],
})
export class HistoryMailComponent implements OnChanges, OnInit, OnDestroy {
  breakpoints: IBreakpoint[] = [];
  tabsCount: number = 1;
  private _historyTabCount$ = new BehaviorSubject<number>(1);
  historyTabCount$ = this._historyTabCount$.asObservable();

  public internalEvents: IEpticaMailContent[];

  hiddenMailInfoBlock: boolean = true;

  currentEmail: IEpticaMailContent;
  requestPromise: IPromise<IEpticaRequestFrame>;
  mailPanelToshow: string = DescriptionStatus.EMAILCONTENT;
  currentDescriptionToshow: boolean;
  currentInfoToshow: boolean;
  currentAttachmentToshow: boolean;
  indexCurrentMailOpen: number = -1;

  sliderCount: number = 0;
  remainingEmailRight: number = 0;
  remainingEmailLeft: number = 0;
  clickButton: number = 0;
  resultLeft: boolean = true;
  resultRight: boolean = false;
  hideMailInfo: boolean = false;
  blockToDisplayinternalEvents: IEpticaMailContent[];
  maxBlockNbToDisplayInternalEvents: number;
  indexStartBlock: number = 0;
  indexEndBlock: number = 0;
  historyFirstDisplay: number = 0;
  clickButtonPrev: number = 0;
  clickButtonNext: number = 1;
  selectMailIndex: number = 1;
  sensor: any;
  tabData: ITabData;
  tabWidth = 178;

  @ViewChild('mailHistoryList', { static: true }) mailHistoryList: ElementRef;
  @Input() epticaEvents: IEpticaExctractEvent[];
  @Input() memo: Memo;
  @Input() responseId: string;
  @Output() public expandZoneResponse = new EventEmitter<boolean>();

  constructor(
    public readonly logger: Logger,
    @Inject(I18NEXT_SERVICE) public $i18next: ITranslationService,
    @Inject(NG1_CONTACT) private contactDeprecatedService: ContactDeprecatedService,
    @Inject(NG1_EPTICA) private epticaDeprecatedService: EpticaDeprecatedService,
    public readonly timeUtil: MomentService
  ) {
    this.logger = this.logger.forSource('HistoryMailComponent');
  }

  ngOnInit() {
    const mailHistoryListWidth = this.mailHistoryList?.nativeElement?.clientWidth || this.tabWidth;
    this._historyTabCount$.next(Math.round(mailHistoryListWidth / this.tabWidth));
    this.historyTabCount$.pipe(destroy(this)).subscribe((count: number) => {
      this.tabsCount = count;
      this.indexStartBlock = 0;
      this.indexEndBlock = this.tabsCount;
      this.renderSlider();
    });
    this.indexCurrentMailOpen = 0;
    this.currentDescriptionToshow = true;
    this.historyFirstDisplay = 0;
    this.requestPromise = this.contactDeprecatedService.getContact(this.responseId).then(contact =>
      this.epticaDeprecatedService.loadRequest({
        requestId: contact.epticaRequestId || 0,
        responseId: this.responseId,
      })
    );
    this.sensor = new ResizeSensor(this.mailHistoryList.nativeElement, () => {
      this.setTabData(this.mailHistoryList.nativeElement.clientWidth, this.breakpoints);
      if (this.tabData && this.tabData.count) {
        this._historyTabCount$.next(this.tabData.count);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.internalEvents = Option.of(this.epticaEvents)
      .map(events =>
        [...events]
          .sort((a, b) => b.date.getTime() - a.date.getTime())
          .map((evt, index) => ({
            evt: evt,
            disabled: this.isDisabled(evt),
            open: index === 0,
            conversational: this.isConversational(evt),
            allAttachment: this.allAttachment(evt),
            index: index,
            isHtmlFormat: !evt.isPlainTextFormat,
          }))
      )
      .getOrElse([]);
    if (changes['epticaEvents']?.currentValue?.length) {
      this.generateBreakpoints(this.epticaEvents.length);
      this.setTabData(this.mailHistoryList.nativeElement.clientWidth, this.breakpoints);
      if (this.tabData && this.tabData.count) {
        this._historyTabCount$.next(this.tabData.count);
      }
    }
  }

  renderSlider(): void {
    this.clickButton = 0;
    this.remainingEmailLeft = 0;
    this.currentEmail = this.internalEvents[0];
    this.resultLeft = this.arrowLeftClicable();
    this.resultRight = this.arrowRightClicable();
    this.maxBlockNbToDisplayInternalEvents = Math.ceil(this.internalEvents.length / this.tabsCount);
    this.blockToDisplayinternalEvents = this.internalEvents.slice(this.indexStartBlock, this.indexEndBlock);
    this.indexCurrentMailOpen = 0;
  }

  generateBreakpoints(count: number): void {
    this.breakpoints = [];
    for (let i = 1; i <= count; i++) {
      this.breakpoints.push({
        breakpointName: `SIZE_${i}`,
        breakpointValue: (this.tabWidth * i + i).toString(),
      });
    }
  }

  setTabData(containerWidth: number, breakpoints: IBreakpoint[]): void {
    for (let i = 0; i < breakpoints.length; i++) {
      let idx = i;
      let nextIdx = idx + 1;
      let condition: boolean;
      let minSize = `${this.tabWidth}px`;
      if (this.breakpoints[nextIdx]) {
        condition = containerWidth > +this.breakpoints[i].breakpointValue && containerWidth <= +this.breakpoints[nextIdx].breakpointValue;
      } else {
        condition = containerWidth > +this.breakpoints[i].breakpointValue;
      }
      if (condition) {
        this.tabData = {
          actualSize: `calc(100% / ${idx + 1})`,
          minSize,
          count: idx + 1,
        };
        break;
      }
    }
  }

  checkIsCurrentMail(index: number, currentMailIndex: number): boolean {
    return index === currentMailIndex && this.selectMailIndex === this.clickButtonNext && this.selectMailIndex > this.clickButtonPrev;
  }

  extractNameSender(event: IEpticaExctractEvent): string {
    return event.description.actor.from.toString();
  }

  historyLength(): number {
    return this.internalEvents ? this.internalEvents.length : 0;
  }

  _expandZoneResponse() {
    this.expandZoneResponse.emit(this.hideMailInfo);
  }

  hideBlock(): void {
    this.hideMailInfo = true;
    this.hiddenMailInfoBlock = false;
  }

  showEventInfo(event: IEpticaMailContent, index: number): void {
    this.currentEmail = event;

    if (
      this.hiddenMailInfoBlock &&
      index === this.indexCurrentMailOpen &&
      this.selectMailIndex === this.clickButtonNext &&
      this.selectMailIndex > this.clickButtonPrev
    ) {
      this.hideBlock();
    } else {
      this.indexCurrentMailOpen = index;
      this.hideMailInfo = false;
      this.selectMailIndex = this.clickButtonNext;
      if (!this.hiddenMailInfoBlock) {
        this.hiddenMailInfoBlock = true;
      }
    }

    this.currentInfoToshow = false;
    this.currentDescriptionToshow = true;
    this.currentAttachmentToshow = false;
    this.mailPanelToshow = DescriptionStatus.EMAILCONTENT;
  }

  showPannel(value: string): void {
    this.mailPanelToshow = value.trim();
    switch (value) {
      case DescriptionStatus.EMAILINFO:
        this.currentInfoToshow = true;
        this.currentDescriptionToshow = false;
        this.currentAttachmentToshow = false;
        break;
      case DescriptionStatus.EMAILATTACHMENT:
        this.currentAttachmentToshow = true;
        this.currentInfoToshow = false;
        this.currentDescriptionToshow = false;
        break;
      default:
        this.currentDescriptionToshow = true;
        this.currentAttachmentToshow = false;
        this.currentInfoToshow = false;
        break;
    }
  }

  indicatorsSlider_prev(): void {
    this.remainingEmailLeft -= this.tabsCount;
    this.remainingEmailRight += this.tabsCount;
    this.resultLeft = this.arrowLeftClicable();

    this.resultRight = this.arrowRightClicable();
    let lengthElement = Math.ceil(this.internalEvents.length / this.tabsCount);
    this.maxBlockNbToDisplayInternalEvents++;

    if (this.maxBlockNbToDisplayInternalEvents <= lengthElement) {
      this.indexEndBlock = this.indexStartBlock;
      this.indexStartBlock = this.indexEndBlock - this.tabsCount;
      this.blockToDisplayinternalEvents = this.internalEvents.slice(this.indexStartBlock, this.indexEndBlock);
    }
    this.clickButtonPrev--;
    this.clickButtonNext--;
  }

  indicatorsSlider_next(): void {
    this.remainingEmailLeft += this.tabsCount;
    this.resultLeft = this.arrowLeftClicable();

    if (this.clickButton > 0) {
      this.remainingEmailRight -= this.tabsCount;
    }

    this.resultRight = this.arrowRightClicable();
    this.maxBlockNbToDisplayInternalEvents--;

    if (this.maxBlockNbToDisplayInternalEvents > 0) {
      this.indexStartBlock = this.indexEndBlock;
      this.indexEndBlock = this.indexEndBlock + this.tabsCount;
      this.blockToDisplayinternalEvents = this.internalEvents.slice(this.indexStartBlock, this.indexEndBlock);
    }
    this.clickButtonNext++;
    this.clickButtonPrev++;
  }

  arrowLeftClicable(): boolean {
    this.sliderCount = this.internalEvents.length;
    let result: boolean = false;
    if (this.sliderCount === 1 || this.sliderCount <= this.tabsCount) {
      result = true;
    } else {
      if (this.remainingEmailLeft + 1 <= this.tabsCount) {
        result = true;
      }
    }
    return result;
  }

  arrowRightClicable(): boolean {
    this.sliderCount = this.internalEvents.length;

    let result: boolean = false;
    if (this.sliderCount === 1 || this.sliderCount <= this.tabsCount) {
      result = true;
    } else {
      if (this.clickButton === 0) {
        this.remainingEmailRight = this.sliderCount - this.tabsCount;
        this.clickButton++;
      }

      if (this.remainingEmailRight < 1) {
        result = true;
      }
    }

    return result;
  }
  extractReadableDate(dateToConvert: Date): string {
    return this.timeUtil.readableDateShort(dateToConvert);
  }

  extractReadableTime(dateToConvert: Date): string {
    return this.timeUtil.readableTime(dateToConvert);
  }

  getHeaderDate(event: IEpticaExctractEvent) {
    return this.extractReadableDate(event.date) + ' • ' + this.extractReadableTime(event.date);
  }

  private allAttachment(event: IEpticaExctractEvent): IEpticaExctractAttachment[] {
    if (!event) return [];
    return event.attachment.concat(
      Option.of(event.response)
        .map(r => r.attachment)
        .getOrElse([])
    );
  }

  private isDisabled(event: IEpticaExctractEvent): boolean {
    if (this.isConversational(event)) return false;
    else
      return (
        this.isEmpty(event.comment) &&
        this.isEmpty(event.content) &&
        (!isDefined(event.response) || (!!event.response && this.isEmpty(event.response.comment) && this.isEmpty(event.response.content)))
      );
  }

  isEmpty(value: string | null | undefined): boolean {
    return Option.of(value)
      .filter(v => v !== '')
      .isEmpty();
  }

  isIncommingMessage(event: IEpticaExctractEvent): boolean {
    return (
      event.selector === EpticaSelector.request_event_type_client_message ||
      event.selector === EpticaSelector.request_event_type_opening ||
      event.selector === EpticaSelector.request_event_type_monitoring
    );
  }

  private isConversational(event: IEpticaExctractEvent): boolean {
    switch (event.selector) {
      case EpticaSelector.request_event_type_response:
      case EpticaSelector.request_event_type_outgoing_mail:
      case EpticaSelector.request_event_type_client_message:
      case EpticaSelector.request_event_type_monitoring:
        return true;
      // Eptica used the same 'request_event_type_opening' event for the reoping by agent or client
      // we only want to display the reoping by client
      // in the agent case there is no message
      // so we test the message content to determinate the isConversational
      case EpticaSelector.request_event_type_opening:
        return !!event.recipient || !!event.ccRecipient || !!event.subject || !!event.content;
      default:
        return false;
    }
  }

  ngOnDestroy(): void {
    this.sensor.detach();
  }
}
